import { cx } from '@linaria/core';
import { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useEscapeKey } from '../Hooks/useEscapeKey';
import { PAPER_HEIGHTS } from '../theme';
import { useMountTransition } from './transitions';
import "./Dialog.linaria.module.css!=!./Dialog?./Dialog.linaria.module.css?css=H4sIAAAAAAAAA4VV0Y6jOBB8v6/Iy0mzuiXChCSE+xjLmMZxMDZjNwFi7b+vkiGAmVudlJeyq+iurpazZ9jQpqZEX/BkC98aJ1EanVdygPLfX3/tn4TqQGXZViL1UjvAPH5fXBwlOPTyrnwpXavYmFcKhvf1lZLrydy6R4g8U1LoSCI0LuegEexciQeSBfpb51BWY8SNRtC40R1vlIxpalTlC2NLsJFlpexcnrZzN01NHeo+yxaXrHBGdQhvSlJSSEYHECJfMF4LazpdRtwoY3MrCvYR/9xNv/3xx/sTdfqelWkZlzgu0+o4bXgJqvZomZ5amFi7JDk2bse7QvKogIcE+xHv0+nzyc8d+bHycU2a5nZafFhQDOV99sEsJUWbmppvoH/GE5XSAn8JuVFdo9+y24GSonngIHzDhqiXJV5zzhT/IHH89y7andJ2mPuQY8C+ghRX/DNdSEru8gy89w2zQur8kCzhKEJJ3wmnjxvozR1spUwfjTnr0KyiIqqrtT5v4Pew7sx+RNHquGUt2Lmxc0wJK8rPi/WFGSJ3ZaXpJ5FiI9gIezMdLyJFiRvPV7j7dRGHTJfMlhHCgMtOlPNOdKikhmUn6pTWxVnfH/O6kFUat9qdD8UqjDRdLfQzLiOathhWjFMcL4z7EtH/hMkaSpLD4WS+ViSPd/FuPW4h3xam8GYHPKOkviR997mBvjIaIycfkJN9crTQzBJHi0oLU4XoS9B/7dExniuIAx27JMnSEPnnJN9rR/an+Q2xtL1UwG5eASLYyD0nq0Ue7+P4fFzaUIbCwDErQuRbVpZSiwhNm5PTakch4MOWXxhE04SSmhLk4nYUGziLFFSYJ6tUlQol6pvEvhyvNayhQnweuyk8siNBeMpQUWN1D0BgM4kDmys2bNiTybeglEwZEaFEBf+8FGQ8X3XWTevyH0dB4TnkilNw3UF0Ifr27r8eMNDzfxMfaIVCqsQL1ubZyoaZLkIUVM8C12s6bOmT7yzIdq2ot4pXslkQ7JqvtvyvWL8EvwFV+CDMmAcAAA=="
export const DIALOG_TRANSITION = 225;
const rootStyles = "atm_mk_1n9t6rb atm_f3_idpfg4 atm_9s_1txwivl atm_h_1h6ojuz atm_fc_1h6ojuz atm_5j_1y44olf";
const backdropStyles = "atm_mk_stnw88 atm_f3_idpfg4 atm_2d_e2ysee atm_k4_idpfg4 atm_uc_mcdelk";
const paperStyles = "atm_mk_h2mmj6 atm_9s_1txwivl atm_ar_1bp4okc atm_j3_1bmztxg atm_iy_1bmztxg atm_gi_1vi7ecw atm_l1_1wugsn5 atm_2d_1luknn7 atm_5j_1y44olf atm_70_1abdq9r atm_7l_1sy7hev atm_kd_idpfg4 atm_k4_idpfg4 atm_uc_mcdelk";

/* variants must follow the base: on a property conflict Linaria's winner is
   source order, not cx() argument order */
const enteredStyles = "atm_k4_kb7nvz";
const maxWidthXsStyles = "atm_j3_jks73b";
const maxWidthSmStyles = "atm_j3_1ogmpbx";
const fullWidthStyles = "atm_vy_1bmztxg";
const titleStyles = "atm_am_12336oc atm_gi_idpfg4 atm_c8_1k92wuq atm_cs_bfngof atm_g3_yu2284 atm_fr_p9feaj atm_lo_exct8b atm_le_exct8b atm_lk_1tcgj5g atm_ll_1tcgj5g";
const contentStyles = "atm_am_ggq5uc atm_l1_1wugsn5 atm_lo_gktfv atm_le_gktfv atm_lk_1tcgj5g atm_ll_1tcgj5g atm_1y7hn8u_idpfg4";
const actionsStyles = "atm_am_12336oc atm_9s_1txwivl atm_h_1h6ojuz atm_fc_esu3gu atm_cx_ftgil2 atm_lo_ftgil2 atm_le_ftgil2 atm_lk_ftgil2 atm_ll_ftgil2";
export interface DialogProps {
  children: ReactNode;
  /** lands on the paper — the surface every call site actually wants to style */
  className?: string;
  'data-testid'?: string;
  fullWidth?: boolean;
  maxWidth?: 'xs' | 'sm';
  onClose?(): void;
  open: boolean;
  zIndex?: number;
}
const Dialog = (props: DialogProps) => {
  const {
    children,
    className,
    fullWidth = false,
    maxWidth,
    onClose,
    open,
    zIndex = PAPER_HEIGHTS.dialog,
    'data-testid': dataTestId
  } = props;
  const {
    mounted,
    entered
  } = useMountTransition(open, DIALOG_TRANSITION);
  useEscapeKey(open, onClose);
  if (!mounted) return null;
  return createPortal(<div className={cx('mfc-dialog', rootStyles)} style={{
    zIndex
  }}>
            <div className={cx('dialog-backdrop', backdropStyles, entered && enteredStyles)} onClick={onClose} />
            <div aria-modal="true" className={cx('dialog-paper', paperStyles, entered && enteredStyles, maxWidth === 'xs' && maxWidthXsStyles, maxWidth === 'sm' && maxWidthSmStyles, fullWidth && fullWidthStyles, className)} data-testid={dataTestId} role="dialog">
                {children}
            </div>
        </div>, document.body);
};
interface DialogSectionProps {
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}
export const DialogTitle = (props: DialogSectionProps) => {
  const {
    children,
    className,
    'data-testid': dataTestId
  } = props;
  return <h2 className={cx('dialog-title', titleStyles, className)} data-testid={dataTestId}>
            {children}
        </h2>;
};
export const DialogContent = (props: DialogSectionProps) => {
  const {
    children,
    className,
    'data-testid': dataTestId
  } = props;
  return <div className={cx('dialog-content', contentStyles, className)} data-testid={dataTestId}>
            {children}
        </div>;
};
export const DialogActions = (props: DialogSectionProps) => {
  const {
    children,
    className,
    'data-testid': dataTestId
  } = props;
  return <div className={cx('dialog-actions', actionsStyles, className)} data-testid={dataTestId}>
            {children}
        </div>;
};
export default Dialog;